import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { RecurringInvoicesControllerListParams } from '@trellis/api'
import type {
  CreateRecurringInvoice,
  RecurringInvoiceStatus,
  UpdateRecurringInvoice,
} from '@trellis/shared'
import { useState } from 'react'

import { pushEvent } from '@/lib/analytics'
import {
  createRecurringInvoice,
  deleteRecurringInvoice,
  getRecurringInvoice,
  getRecurringInvoiceStats,
  listRecurringInvoices,
  triggerRecurringInvoice,
  updateRecurringInvoice,
  updateRecurringInvoiceStatus,
} from '@/requests/recurring-invoices'

export function useRecurringInvoicesQuery(
  options: RecurringInvoicesControllerListParams | undefined,
) {
  const [pageIndex, setPageIndex] = useState(0)

  const query = useInfiniteQuery({
    queryKey: ['recurring-invoices', options],
    queryFn: ({ pageParam }) =>
      listRecurringInvoices({ ...options, cursor: pageParam ?? options?.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
    enabled: options !== undefined,
  })

  const totalPages = query.data?.pages.length ?? 1
  const currentPageData = query.data?.pages[pageIndex] ?? null
  const isOnLastFetchedPage = pageIndex === totalPages - 1
  const hasNextPage = isOnLastFetchedPage ? (query.hasNextPage ?? false) : true

  function goToNextPage() {
    if (isOnLastFetchedPage) {
      void query.fetchNextPage()
    }
    setPageIndex((previous) => previous + 1)
  }

  function goToPrevPage() {
    setPageIndex((previous) => Math.max(0, previous - 1))
  }

  return {
    ...query,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  }
}

export function useRecurringInvoiceQuery(id: string) {
  return useQuery({
    queryKey: ['recurring-invoices', id],
    queryFn: () => getRecurringInvoice(id),
    enabled: !!id,
  })
}

export function useCreateRecurringInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateRecurringInvoice) => {
      const result = await createRecurringInvoice(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      pushEvent('recurring_invoice_created')
      await queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
    },
    onError: () => pushEvent('form_submit_failed', { form_id: 'recurring_invoice' }),
  })
}

export function useUpdateRecurringInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRecurringInvoice }) => {
      const result = await updateRecurringInvoice(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      await queryClient.invalidateQueries({
        queryKey: ['recurring-invoices', variables.id],
      })
    },
  })
}

export function useUpdateRecurringInvoiceStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string
      status: RecurringInvoiceStatus
    }) => {
      const result = await updateRecurringInvoiceStatus(id, { status })
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      await queryClient.invalidateQueries({
        queryKey: ['recurring-invoices', variables.id],
      })
    },
  })
}

export function useDeleteRecurringInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteRecurringInvoice(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
    },
  })
}

export function useRecurringInvoiceStatsQuery(enabled = true) {
  return useQuery({
    queryKey: ['recurring-invoices', 'stats'],
    queryFn: getRecurringInvoiceStats,
    enabled,
  })
}

export function useTriggerRecurringInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await triggerRecurringInvoice(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}
